import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import ImpresionLocalWatcher from "@/components/ImpresionLocalWatcher";
import NotificadorPagosBancarios from "@/components/NotificadorPagosBancarios";
import NotificadorDispositivosMesera from "@/components/NotificadorDispositivosMesera";
import ErrorBoundaryApp from "@/components/ErrorBoundaryApp";
import ActualizacionPwaWatcher from "@/components/ActualizacionPwaWatcher";
// TODAS las rutas se cargan bajo demanda (incluidas Login y Salón). La
// mayoría de quien visita el sitio es un cliente anónimo entrando a la
// tienda pública (/menu) o al QR de mesa — no tiene sentido que su celular
// descargue el bundle completo del POS (Salón + sus ~15 modales de
// impresión/pedidos) solo para ver el menú. El login/salón de la meseras
// se cargan aparte, solo cuando alguien realmente entra por esa ruta.
const LandingPage = lazy(() => import("@/pages/Landing/LandingPage"));
const MesaQrPage = lazy(() => import("@/pages/MesaQr/MesaQrPage"));
const LoginPage = lazy(() => import("@/pages/Login/LoginPage"));
const SalonPage = lazy(() => import("@/pages/Salon/SalonPage"));
const AdminMesasPage = lazy(() => import("@/pages/AdminMesas/AdminMesasPage"));
const AdminCatalogoPage = lazy(() => import("@/pages/AdminCatalogo/AdminCatalogoPage"));
const AjustesSeguridadPage = lazy(() => import("@/pages/Ajustes/AjustesSeguridadPage"));
const AdminReportesPage = lazy(() => import("@/pages/AdminReportes/AdminReportesPage"));
const DashboardRemotoPage = lazy(() => import("@/pages/AdminRemoto/DashboardRemotoPage"));
const AdminDomiciliosPage = lazy(() => import("@/pages/AdminDomicilios/AdminDomiciliosPage"));
const AdminFiadosPage = lazy(() => import("@/pages/AdminFiados/AdminFiadosPage"));
const AdminDianPage = lazy(() => import("@/pages/AdminDian/AdminDianPage"));
const AdminInstaladorPage = lazy(() => import("@/pages/AdminInstalador/AdminInstaladorPage"));
const AdminInsumosPage = lazy(() => import("@/pages/AdminInsumos/AdminInsumosPage"));
const AdminCajasPage = lazy(() => import("@/pages/AdminCajas/AdminCajasPage"));
const AdminEmpresaPage = lazy(() => import("@/pages/AdminEmpresa/AdminEmpresaPage"));
const AdminAuditoriaMesasPage = lazy(() => import("@/pages/AdminAuditoriaMesas/AdminAuditoriaMesasPage"));
const AdminDispositivosPage = lazy(() => import("@/pages/AdminDispositivos/AdminDispositivosPage"));
const AdminWhatsappPage = lazy(() => import("@/pages/AdminWhatsapp/AdminWhatsappPage"));
const AdminPagosBancariosPage = lazy(() => import("@/pages/AdminPagosBancarios/AdminPagosBancariosPage"));
const AdminGastosPersonalesPage = lazy(() => import("@/pages/AdminGastosPersonales/AdminGastosPersonalesPage"));

function CargandoPagina() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-crema">
      <p className="text-texto-secundario">Cargando…</p>
    </div>
  );
}

export default function App() {
  const { estado, usuario } = useAuth();
  const location = useLocation();
  // Estas páginas las ve el CLIENTE (escaneando el QR de su mesa, o el menú
  // público) — aunque el navegador/tablet tenga guardada una sesión de admin
  // (p.ej. la misma tablet se usa para mostrar el QR y para operar la caja),
  // nunca deben aparecer avisos internos (pagos bancarios, dispositivos por
  // aprobar) encima de lo que el cliente está viendo.
  const esRutaPublica =
    location.pathname.startsWith("/menu") ||
    location.pathname.startsWith("/mesa/") ||
    location.pathname === "/login";

  useEffect(() => {
    // Si llegamos hasta aquí es que la app cargó bien: libera la bandera
    // de "ya recargué por un chunk desactualizado" para que un futuro
    // despliegue pueda volver a disparar la recarga automática si hace falta.
    try {
      sessionStorage.removeItem("tentacion_recarga_por_error_chunk");
    } catch {
      // localStorage/sessionStorage puede no estar disponible (modo privado); no es crítico.
    }
  }, []);

  if (estado.tipo === "cargando") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-crema">
        <p className="text-texto-secundario">Cargando…</p>
      </div>
    );
  }

  return (
    <ErrorBoundaryApp>
      <ActualizacionPwaWatcher />
      {usuario && <ImpresionLocalWatcher />}
      {usuario && !esRutaPublica && (usuario.rol === "ADMIN" || (usuario.rol === "CAJERO" && usuario.notificarPagosBancarios)) && <NotificadorPagosBancarios />}
      {usuario?.rol === "ADMIN" && !esRutaPublica && <NotificadorDispositivosMesera />}
      <Suspense fallback={<CargandoPagina />}>
      <Routes>
      {/* Rutas Públicas */}
      <Route path="/menu" element={<LandingPage />} />
      <Route path="/menu/categoria/:categoriaId" element={<LandingPage />} />
      <Route path="/mesa/:qrToken" element={<MesaQrPage />} />
      <Route path="/login" element={usuario ? <Navigate to="/salon" replace /> : <LoginPage />} />

      {/* Rutas Autenticadas POS */}
      {usuario ? (
        <>
          <Route path="/" element={<Navigate to="/salon" replace />} />
          <Route path="/salon" element={<SalonPage />} />
          <Route
            path="/admin/instalador"
            element={usuario.rol === "ADMIN" || usuario.rol === "CAJERO" ? <AdminInstaladorPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/domicilios"
            element={usuario.rol === "ADMIN" || usuario.rol === "CAJERO" || usuario.rol === "MESERA" ? <AdminDomiciliosPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/fiados"
            element={usuario.rol === "ADMIN" || usuario.rol === "CAJERO" ? <AdminFiadosPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/cajas"
            element={usuario.rol === "ADMIN" || usuario.rol === "CAJERO" ? <AdminCajasPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/dian"
            element={usuario.rol === "ADMIN" ? <AdminDianPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/mesas"
            element={usuario.rol === "ADMIN" ? <AdminMesasPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/catalogo"
            element={usuario.rol === "ADMIN" ? <AdminCatalogoPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/insumos"
            element={usuario.rol === "ADMIN" ? <AdminInsumosPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/seguridad"
            element={usuario.rol === "ADMIN" ? <AjustesSeguridadPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/reportes"
            element={usuario.rol === "ADMIN" || usuario.rol === "CAJERO" ? <AdminReportesPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/empresa"
            element={usuario.rol === "ADMIN" ? <AdminEmpresaPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/auditoria-mesas"
            element={usuario.rol === "ADMIN" ? <AdminAuditoriaMesasPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/dispositivos"
            element={usuario.rol === "ADMIN" ? <AdminDispositivosPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/whatsapp"
            element={usuario.rol === "ADMIN" || usuario.rol === "CAJERO" ? <AdminWhatsappPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/pagos-bancarios"
            element={usuario.rol === "ADMIN" || usuario.rol === "CAJERO" ? <AdminPagosBancariosPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/remoto"
            element={usuario.rol === "ADMIN" ? <DashboardRemotoPage /> : <Navigate to="/salon" replace />}
          />
          <Route
            path="/admin/gastos-personales"
            element={usuario.rol === "ADMIN" ? <AdminGastosPersonalesPage /> : <Navigate to="/salon" replace />}
          />
          <Route path="*" element={<Navigate to="/salon" replace />} />
        </>
      ) : (
        <>
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/menu" replace />} />
        </>
      )}
    </Routes>
      </Suspense>
    </ErrorBoundaryApp>
  );
}
